import * as React from 'react';
import SectionedTableRow, { SectionedTableRowType } from './SectionedTableRow';
import { formatCurrencyWithCommas } from '../../../util/tools';

export enum SectionedTableType {
    Text = 'text',
    Currency = 'currency'
}

export interface ISectionedTableProps {
    data: any;
    viewData: Record<string, string>;
    type?: SectionedTableType;
    className?: string;
    excludedKeys?: string[];
}

const formatValue = (value: any, tableType?: SectionedTableType) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    if (tableType === SectionedTableType.Currency) {
        return formatCurrencyWithCommas(value);
    }

    return value;
};

const getObjectKeys = (item: any) => {
    if (!item || typeof item !== 'object') {
        return [];
    }

    return Object.keys(item);
};

const getItemLabel = (item: any) => {
    const keys = getObjectKeys(item);

    if (keys.length === 0) {
        return '';
    }

    return item[keys[0]];
};

const getItemValue = (item: any) => {
    const keys = getObjectKeys(item);

    if (keys.length === 0) {
        return '';
    }

    return item[keys[keys.length - 1]];
};

export const SectionedTable: React.FC<ISectionedTableProps> = (props) => {
    const {
        data,
        viewData,
        type = SectionedTableType.Text,
        className,
        excludedKeys = []
    } = props;

    const orderedKeys = Object.keys(viewData).filter(key => !excludedKeys.includes(key));

    return (
        <div className={className || 'sectionedTable'}>
            {orderedKeys.map((key, index) => {
                const label = viewData[key];
                const value = key === 'paymentAmount' ? data?.payment : data?.[key];
                const isLastSection = index === orderedKeys.length - 1;

                const nextKey = orderedKeys[index + 1];
                const nextValue = nextKey
                    ? nextKey === 'paymentAmount'
                        ? data?.payment
                        : data?.[nextKey]
                    : undefined;
                const nextIsArray = Array.isArray(nextValue);

                if (Array.isArray(value)) {
                    return (
                        <div key={key} className="sectionedTable__section">
                            <SectionedTableRow
                                label={`${label}:`}
                                rowType={SectionedTableRowType.GroupHeader}
                            />

                            {value.map((item, itemIndex) => (
                                <React.Fragment key={`${key}-${itemIndex}`}>
                                    <SectionedTableRow
                                        label={getItemLabel(item)}
                                        value={formatValue(getItemValue(item), type)}
                                        rowType={SectionedTableRowType.GroupItem}
                                    />
                                    {itemIndex !== value.length - 1 && (
                                        <div className="sectionedTable__thinDivider" />
                                    )}
                                </React.Fragment>
                            ))}

                            {!isLastSection && (
                                <>
                                    <div className="sectionedTable__spacer" />
                                    {!nextIsArray && <div className="sectionedTable__divider" />}
                                </>
                            )}
                        </div>
                    );
                }

                return (
                    <div key={key} className="sectionedTable__section">
                        <SectionedTableRow
                            label={`${label}:`}
                            value={formatValue(value, type)}
                            rowType={SectionedTableRowType.Single}
                        />

                        {!isLastSection && (
                            <div className="sectionedTable__divider" />
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default SectionedTable;